import { GrundstueckId } from '../../../../core/gb-api';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ComponentLocationStrategy } from '../../../services/location-strategy/component-location-strategy';
import { GrundstueckBeschreibungLocationStrategy } from '../../../services/location-strategy/grundstueck-detail-beschreibung-location-strategy.';
import { LinkComponent, NavTarget } from '../../../components/link/link.component';
import { AvgebaeudeService, FieldMetaData, GebaeudeMasterDetailEntryMeta, GebaeudeVersionDetailEntryMeta } from '../../../../core/av-api';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { AbstractComponent } from '../../../components/abstract-component/abstract.component';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { ComparatorService } from '../../../services/comparator-service';
import { FormatConfigService } from 'app/core/services/format-config.service';
import { ErweiterungConfigResponse } from 'app/core/base-api';
import { ListLoader } from '../../../components/list-loader/list-loader';
import { ListLoaderObservable } from '../../../components/list-loader/list-loader-observable';
import { map } from 'rxjs/operators';
import { KompaktGebaeudeMasterPipe } from 'app/shared/kompaktdarstellung/gebaeude/kompakt-gebaeude-master-pipe';
import { TableSortModel } from 'app/shared/tablesort/tableSortModel';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { BeschreibungTableWrapperComponent } from '../../../components/table/beschreibung-table-wrapper.component';
import { ListLoaderWrapperComponent } from '../../../components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../../tablesort/sort-icon/column-header.component';
import { HistoryDirective } from '../../../directives/history.directive';
import { KompaktTimepointComponent } from '../../../kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { GebaeudeGehoertZuSdrComponent } from '../../../components/gebaeude-gehoert-zu-sdr/gebaeude-gehoert-zu-sdr.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DecimalHochkommaPipe } from '../../../pipes/simple/decimalHochkomma.pipe';
import { KompaktGebaeudeMasterPipe as KompaktGebaeudeMasterPipe_1 } from '../../../kompaktdarstellung/gebaeude/kompakt-gebaeude-master-pipe';
import { KompaktVersionGebaeudeeingangsadressePipe } from '../../../kompaktdarstellung/gebaeudeeingangsadresse/kompakt-version-gebaeudeeingangsadresse-pipe';
import { OrderByErstellzeitpunktPipe } from '../../../pipes/order/order-by-erstellzeitpunkt.pipe';
import { GebaeudeMasterDetailEntryVmService } from '../../../services/gebaeude/gebaeude-master-detail-entry-vm.service';
import { GwrLinkComponent } from '../../../components/gwr-link/gwr-link.component';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-gebaeude',
  templateUrl: './gebaeude.component.html',
  styleUrls: ['./gebaeude.component.scss'],
  providers: [{ provide: ComponentLocationStrategy, useClass: GrundstueckBeschreibungLocationStrategy }],
  imports: [
    BeschreibungTableWrapperComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    HistoryDirective,
    LinkComponent,
    KompaktTimepointComponent,
    GebaeudeGehoertZuSdrComponent,
    TranslatePipe,
    DecimalHochkommaPipe,
    KompaktGebaeudeMasterPipe_1,
    KompaktVersionGebaeudeeingangsadressePipe,
    OrderByErstellzeitpunktPipe,
    GwrLinkComponent
  ]
})
export class GebaeudeComponent extends AbstractComponent implements OnChanges {
  avPlusErweiterung = false;
  navTargets = NavTarget;
  gebaeude: GebaeudeMasterDetailEntryMeta[];
  listLoader: ListLoader<GebaeudeMasterDetailEntryVmService>;
  tableSortModel = new TableSortModel<GebaeudeMasterDetailEntryVmService>('table', this.comparatorService);
  hasRightToNavigateGebaeude: boolean;
  @Input()
  hasRight = true;
  @Input()
  anzeigenAusSichtGeschaeft = false;
  @Input()
  isMeldung = false;
  navigateTo = NavTarget.NAV_GRUNDSTUECK;
  showVersichertenNummer = false;
  showGesamtFlaeche = false;

  constructor(
    private gebaeudeService: AvgebaeudeService,
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private comparatorService: ComparatorService,
    private appFormatConfigService: FormatConfigService,
    private kompaktGebaeudeMasterPipe: KompaktGebaeudeMasterPipe
  ) {
    super();
    keysToTranslate(['geometrischAuf']);
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.avPlusErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_PLUS);
      })
    );
    this.addSub(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        this.update();
      })
    );
    this.tableSortModel
      .addColumn('egid', (a, b) =>
        comparatorService.orderByGebaeudeComparator(
          a.lastVersion.model.gwrEgid,
          b.lastVersion.model.gwrEgid,
          a.lastVersion.model,
          b.lastVersion.model
        )
      )
      .switchSortDirection();
    this.tableSortModel.addColumn('be-egid', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.kantGid,
        b.lastVersion.model.kantGid,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('versicherungsnummer', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.versicherungsNummer,
        b.lastVersion.model.versicherungsNummer,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('beschreibung', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        this.getKompaktString(a),
        this.getKompaktString(b),
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
    this.tableSortModel.addColumn('adresse', (a, b) =>
      comparatorService.eingangadressenComparator(a.getRelevantEingangsAdresse, b.getRelevantEingangsAdresse)
    );
    this.tableSortModel.addColumn('teilflaeche', (a, b) =>
      comparatorService.orderByComparator(
        a.lastVersion.gebaeudeAufGrundstueckZuVersion[0]?.flaecheQuadratmeter,
        b.lastVersion.gebaeudeAufGrundstueckZuVersion[0]?.flaecheQuadratmeter
      )
    );
    this.tableSortModel.addColumn('gesamtflaeche', (a, b) =>
      comparatorService.orderByGebaeudeComparator(
        a.lastVersion.model.totalQuadratmeter,
        b.lastVersion.model.totalQuadratmeter,
        a.lastVersion.model,
        b.lastVersion.model
      )
    );
  }

  _grundstueckId: GrundstueckId;

  get grundstueckId(): GrundstueckId {
    return this._grundstueckId;
  }

  @Input()
  set grundstueckId(value: GrundstueckId) {
    this._grundstueckId = value;
    if (!value) {
      return;
    }
    this.hasRightToNavigateGebaeude = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.grundstueckId?.currentValue) {
      return;
    }
    this.update();
  }

  update(): void {
    const historischeDaten = this.getHistorischeDaten();
    const timepointCtx = this.timepointService.timepointCtx;
    if (this.hasRight && this._grundstueckId) {
      if (this.isMeldung) {
        const gebaeudeObservable = this.gebaeudeService
          .loadGebaeudeSichtGbMeldung(
            //
            this._grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            map((gebaeude) => {
              this.gebaeude = gebaeude;
              return this.createViewModel(gebaeude);
            })
          );
        this.listLoader = new ListLoaderObservable<GebaeudeMasterDetailEntryVmService>(undefined, 1, gebaeudeObservable);
        this.listLoader.applySortModel(this.tableSortModel);
      } else {
        const gebaeudeObservable = this.gebaeudeService
          .loadGebaeudeSichtGrundstueck(
            //
            this._grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            map((gebaeude) => {
              this.gebaeude = gebaeude;
              return this.createViewModel(gebaeude);
            })
          );
        this.listLoader = new ListLoaderObservable<GebaeudeMasterDetailEntryVmService>(undefined, 1, gebaeudeObservable);
        this.listLoader.applySortModel(this.tableSortModel);
      }
    }
  }

  createViewModel(gebaeude: GebaeudeMasterDetailEntryMeta[]): GebaeudeMasterDetailEntryVmService[] {
    const gebaeudeVM = [];
    for (const geb of gebaeude) {
      const versions = geb.versions;
      geb.versions = null;
      if (versions?.length) {
        const gebaeudeMaster = new GebaeudeMasterDetailEntryVmService(geb, versions, this.comparatorService, this._grundstueckId, false);
        if (gebaeudeMaster.filteredGebaeudeAufGrundstueck?.length > 0) {
          gebaeudeVM.push(gebaeudeMaster);
        }
      }
    }
    this.showVersichertenNummer = gebaeudeVM[0]?.versions[0].mVersicherungsNummer?.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    this.showGesamtFlaeche = gebaeudeVM[0]?.versions[0].mTotalQuadratmeter?.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    return gebaeudeVM;
  }

  getQueryParams(gebaeudeMaster: GebaeudeMasterDetailEntryMeta, gebaeudeVersion: GebaeudeVersionDetailEntryMeta) {
    if (!gebaeudeMaster || !gebaeudeMaster.id) {
      return;
    }
    return { id: gebaeudeMaster.id.id, versionId: gebaeudeVersion.id.id };
  }

  private getKompaktString(gebaeudeVm: GebaeudeMasterDetailEntryVmService) {
    return this.kompaktGebaeudeMasterPipe.transform(gebaeudeVm.lastVersion.model, null);
  }

  private getHistorischeDaten() {
    if (this.anzeigenAusSichtGeschaeft && this.appConfigService.einstellungHistorischeDaten === HistoryMode.HistoryMode.OHNE_HIST) {
      return HistoryMode.HistoryMode.OHNE_HIST_MIT_GESCHAEFTSAENDERUNGEN;
    }
    return this.appConfigService.einstellungHistorischeDaten;
  }
}
